import { InfoCircleOutlined } from "@ant-design/icons"
import {
  Button,
  Col,
  Flex,
  Form,
  Row,
  Space,
  Switch,
  Tooltip,
  Typography,
  type UploadFile
} from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import dayjs from "dayjs"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { CustomDatePicker } from "../../../../components/Date&TimePickers/CustomDatePicker"
import InputNumber from "../../../../components/Input/InputNumber"
import { TextInput } from "../../../../components/Input/TextInput.tsx"
import { PhoneInput } from "../../../../components/PhoneInputs/PhoneInput"
import SelectComponent from "../../../../components/Select"
import { placeholders } from "../../../../constants/placeholders"
import { LOCAL_STATES } from "../../../../constants/states"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { normalizeEmail } from "../../../../utils/form/normalizeEmail.tsx"
import { validatePhone } from "../../../../utils/validation/validate-phone"
import { ComplianceDocumentUploadModal } from "../ComplianceDocumentUploadModal.tsx"
import { fetchSubcontractorsFilterOptions } from "../../redux/subcontractors.thunks"
import { DocumentUpload } from "./DocumentUpload.tsx"
import { type SubcontractorFormData } from "./subcontractor-form-data.ts"
import {
  type SubcontractorFormValues,
  toSubcontractorFormData
} from "./subcontractor-form-submit.ts"

const { Text, Title } = Typography

const ENTITY_TYPE_OPTIONS = [
  { label: "LLC", value: "llc" },
  { label: "Corp", value: "corp" },
  { label: "S-Corp", value: "s_corp" },
  { label: "Sole Prop", value: "sole_prop" },
  { label: "Partnership", value: "partnership" }
]

const EIN_PATTERN = /^\d{2}-?\d{7}$/
const SSN_PATTERN = /^\d{3}-?\d{2}-?\d{4}$/

const CSLB_STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Suspended", value: "suspended" },
  { label: "Expired", value: "expired" },
  { label: "Inactive", value: "inactive" }
]
// The CSLB's rule is numeric, up to 8 digits — real licenses run 6-7 digits.
const CSLB_LICENSE_NUMBER_PATTERN = /^\d{1,8}$/
const CSLB_MINIMUM_BOND_AMOUNT = 25000

// A plain `type`/`min`/`transform` rule runs `transform` even on an untouched
// (undefined) field, turning it into 0/NaN and tripping `min` on a field nobody
// filled in — a custom validator short-circuits first. Shared by the contractor's
// bond and the qualifying individual's, which have the same $25,000 minimum.
const validateBondAmount = (_rule: unknown, value: unknown) => {
  if (value === undefined || value === null || value === "") {
    return Promise.resolve()
  }
  if (Number(value) < CSLB_MINIMUM_BOND_AMOUNT) {
    return Promise.reject(
      new Error(
        `Minimum bond amount is $${CSLB_MINIMUM_BOND_AMOUNT.toLocaleString()}`
      )
    )
  }
  return Promise.resolve()
}

const PAYMENT_METHOD_OPTIONS = [
  { label: "ACH", value: "ach" },
  { label: "Wire", value: "wire" },
  { label: "Check", value: "check" }
]
const ACH_ROUTING_NUMBER_PATTERN = /^\d{9}$/
const ACH_ACCOUNT_NUMBER_PATTERN = /^\d{4,17}$/

const normFile = (e: { fileList?: UploadFile[] } | UploadFile[]) =>
  Array.isArray(e) ? e : e?.fileList

interface SubcontractorFormProps {
  loading?: boolean
  initialValues?: Partial<SubcontractorFormData> & {
    id?: string
    ein_last4?: string | null
    ssn_last4?: string | null
    cslb_expiration_date?: string | null
    ach_routing_number_last4?: string | null
    ach_account_number_last4?: string | null
  }
  /** The name a caller creating inline (e.g. from a finance row) already typed. */
  defaultName?: string
  innerForm?: FormInstance
  onSave: (data: SubcontractorFormData) => void
  onCancel: () => void
}

/**
 * An expiration date belongs to a document: without the file there is nothing
 * to track it against, and the API refuses it. The document itself is
 * optional, so the date is only checked when one is entered.
 */
const dateNeedsItsDocument =
  (
    documentField:
      | "workers_comp_document"
      | "general_liability_document"
      | "license_document"
  ) =>
  ({ getFieldValue }: { getFieldValue: (name: string) => unknown }) => ({
    validator(_: unknown, value: unknown) {
      const files = getFieldValue(documentField) as unknown[] | undefined
      if (value && !files?.length) {
        return Promise.reject(
          new Error("Upload the document this expiration date belongs to")
        )
      }
      return Promise.resolve()
    }
  })

export const SubcontractorForm = ({
  loading,
  initialValues,
  defaultName,
  innerForm,
  onSave,
  onCancel
}: SubcontractorFormProps) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [form] = Form.useForm<SubcontractorFormValues>(innerForm)

  const fetching = useAppSelector((state) => state.subcontractors.saveFetching)
  const filterOptions = useAppSelector(
    (state) => state.subcontractors.filterOptions
  )

  const permissions = useAppSelector(
    (state) => state?.auth?.permissions?.codes?.subcontractors
  )

  const inviteSubcontractors = permissions?.features?.subcontractor_invite
  const createSubcontractors = permissions?.subcontractors?.subcontractor_create

  const defaultSendInvitation = useMemo(() => {
    if (inviteSubcontractors && !createSubcontractors) return true
    return !(!inviteSubcontractors && createSubcontractors)
  }, [createSubcontractors, inviteSubcontractors])

  const showToggle =
    !initialValues && inviteSubcontractors && createSubcontractors

  const sendInvitation = Form.useWatch("send_invitation", form)
  const entityType = Form.useWatch("entity_type", form)
  const paymentMethod = Form.useWatch("payment_method", form)

  // Every section is offered from the start, whether the subcontractor is
  // added directly or invited: the invitation only decides whether an email
  // goes out, so the GC can enter whatever they already know either way.
  // Compliance documents are uploaded here only when creating — an existing
  // profile goes through the dedicated upload action.
  const showComplianceSection = !initialValues
  const [managingLicense, setManagingLicense] = useState(false)
  // This form owns no refetch of its own (the parent's `initialValues` prop
  // is what's authoritative), so a successful upload is reflected here only
  // — an immediate answer to "did that work?" without waiting on the parent
  // to re-read the record, which still happens the normal way once this
  // modal (and, usually, the whole edit form) closes.
  // `undefined` means "nothing uploaded in this session, trust initialValues";
  // `null` means "just uploaded a document that genuinely has no expiry". Both
  // used to be `null`, so uploading a dateless document fell through the `??`
  // below and kept showing the previous document's date — a date that no
  // longer belonged to anything on file.
  const [licenseExpirationOverride, setLicenseExpirationOverride] =
    useState<string | null | undefined>(undefined)
  const normalizedInitialValues = useMemo(() => {
    // `defaultName` is a name a caller creating inline (say from a finance row)
    // already typed.
    if (!initialValues)
      return { send_invitation: defaultSendInvitation, first_name: defaultName }
    return {
      ...initialValues,
      w9_validity_date: initialValues.w9_validity_date
        ? dayjs(initialValues.w9_validity_date)
        : undefined,
      cslb_issue_date: initialValues.cslb_issue_date
        ? dayjs(initialValues.cslb_issue_date)
        : undefined,
      // The API returns the existing W-9 as a URL string, not the fileList
      // shape the Upload's `valuePropName="fileList"` expects — handing it
      // one crashes antd's Upload ("(fileList || []).forEach is not a
      // function"). Editing always starts the field empty; the existing
      // file is not lost, just not prefilled — a new upload replaces it.
      w9_document: undefined
    }
  }, [initialValues, defaultSendInvitation, defaultName])

  const onFinish = (values: SubcontractorFormValues) =>
    onSave(toSubcontractorFormData(values, initialValues))

  useEffect(() => {
    dispatch(fetchSubcontractorsFilterOptions())
  }, [dispatch])

  return (
    <Form<SubcontractorFormValues>
      form={form}
      layout="vertical"
      onFinish={onFinish}
      validateTrigger={["onSubmit", "onChange"]}
      className="with-default-error"
      initialValues={normalizedInitialValues}
    >
      <Flex
        vertical
        className="pb-4 overflow-auto overflow-x-hidden tailwind-scroll max-h-[75vh]"
      >
        {/* Name Fields */}
        <Row gutter={12}>
          <Col xs={24} sm={24} md={8}>
            <Form.Item<SubcontractorFormValues>
              label="First Name"
              name="first_name"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Please enter subcontractor first name"
                }
              ]}
            >
              <TextInput placeholder="Enter first name" size="large" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item<SubcontractorFormValues>
              label="Middle Name"
              name="middle_name"
              rules={[
                {
                  required: false,
                  whitespace: true,
                  message: "Please enter subcontractor middle name"
                }
              ]}
            >
              <TextInput placeholder="Enter middle name" size="large" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item<SubcontractorFormValues>
              label="Last Name"
              name="last_name"
              rules={[
                {
                  required: false,
                  whitespace: true,
                  message: "Please enter subcontractor last name"
                }
              ]}
            >
              <TextInput placeholder="Enter last name" size="large" />
            </Form.Item>
          </Col>
        </Row>

        {/* Contact Fields */}
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item<SubcontractorFormValues>
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Please enter email" },
                { type: "email", message: "Please enter a valid email" }
              ]}
              normalize={normalizeEmail}
            >
              <TextInput
                placeholder={placeholders.email}
                size="large"
                disableAutoCapitalize
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item<SubcontractorFormValues>
              label="Phone Number"
              name="phone"
              // Mandatory when adding; an existing subcontractor without one
              // can still be edited without being forced to supply it.
              rules={[{ required: !initialValues, validator: validatePhone }]}
            >
              <PhoneInput placeholder={placeholders.phone} size="large" />
            </Form.Item>
          </Col>
        </Row>

        {/* Price, Locale & Specialization */}
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item<SubcontractorFormValues>
              label="Price per hour"
              name="price_per_hour"
              rules={[
                { required: false, message: "Please enter your price" },
                {
                  type: "number",
                  min: 0,
                  message: "Value must be positive"
                }
              ]}
            >
              <InputNumber
                size="large"
                prefix="$"
                style={{ width: "100%" }}
                placeholder="0"
                min={0}
                precision={2}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item<SubcontractorFormValues>
              label="Location"
              name="location"
              rules={[
                {
                  required: false,
                  message: "Please select location"
                }
              ]}
            >
              <SelectComponent
                size="large"
                mode={"multiple"}
                maxTagCount={"responsive"}
                placeholder="Select location"
                options={filterOptions?.["location"] || []}
                optionFilterType="form"
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input?.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item<SubcontractorFormValues>
          label="Specialization"
          name="specialization"
          rules={[
            {
              required: false,
              message: "Please select subcontractor specialization"
            }
          ]}
        >
          <SelectComponent
            size="large"
            placeholder="Select custom specialization"
            options={filterOptions?.["specialization"] || []}
            optionFilterType="form"
            mode="multiple"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input?.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item hidden={!showToggle}>
          <Space>
            <Form.Item<SubcontractorFormValues>
              noStyle
              name="send_invitation"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Text>Send invitation</Text>
            <Tooltip title="If disabled, the subcontractor will be added without invitation.">
              <InfoCircleOutlined />
            </Tooltip>
          </Space>
        </Form.Item>

        {showComplianceSection && (
          <>
            <Title level={5} className={"!mb-2 !mt-2"}>
              Compliance Documents
            </Title>
            <Text type="secondary" className={"mb-3 block"}>
              Optional. Any document with an expiration date is tracked, and you
              are alerted before it lapses.
            </Text>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item<SubcontractorFormValues>
                  label="Workers' Comp Document"
                  name="workers_comp_document"
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                >
                  <DocumentUpload />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item<SubcontractorFormValues>
                  label="Workers' Comp Expiration Date"
                  name="workers_comp_expiration_date"
                  dependencies={["workers_comp_document"]}
                  rules={[dateNeedsItsDocument("workers_comp_document")]}
                >
                  <CustomDatePicker
                    className={"w-full"}
                    size="large"
                    format="YYYY-MM-DD"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item<SubcontractorFormValues>
                  label="General Liability Document"
                  name="general_liability_document"
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                >
                  <DocumentUpload />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item<SubcontractorFormValues>
                  label="General Liability Expiration Date"
                  name="general_liability_expiration_date"
                  dependencies={["general_liability_document"]}
                  rules={[dateNeedsItsDocument("general_liability_document")]}
                >
                  <CustomDatePicker
                    className={"w-full"}
                    size="large"
                    format="YYYY-MM-DD"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item<SubcontractorFormValues>
                  label="License Document"
                  name="license_document"
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                >
                  <DocumentUpload />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item<SubcontractorFormValues>
                  label="License Expiration Date"
                  name="license_expiration_date"
                  dependencies={["license_document"]}
                  rules={[dateNeedsItsDocument("license_document")]}
                >
                  <CustomDatePicker
                    className={"w-full"}
                    size="large"
                    format="YYYY-MM-DD"
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        <>
          <Title level={5} className={"!mb-2 !mt-2"}>
            Identity & Tax
          </Title>
          <Text type="secondary" className={"mb-3 block"}>
            Optional — fill in what you have.
          </Text>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="Legal Business Name"
                name="legal_business_name"
              >
                <TextInput
                  placeholder="Enter legal business name"
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="Entity Type"
                name="entity_type"
              >
                <SelectComponent
                  size="large"
                  placeholder="Select entity type"
                  options={ENTITY_TYPE_OPTIONS}
                  optionFilterType="form"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="Business Address"
                name="business_address"
              >
                <TextInput placeholder="Street address" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="City"
                name="business_city"
              >
                <TextInput placeholder="City" size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="State"
                name="business_state"
              >
                <SelectComponent
                  size="large"
                  placeholder="Select state"
                  options={LOCAL_STATES}
                  optionFilterType="form"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input?.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="ZIP Code"
                name="business_zip_code"
              >
                <TextInput
                  placeholder="ZIP code"
                  size="large"
                  disableAutoCapitalize
                />
              </Form.Item>
            </Col>
          </Row>

          {entityType === "sole_prop" ? (
            <Form.Item<SubcontractorFormValues>
              label="SSN"
              name="ssn"
              rules={[
                {
                  pattern: SSN_PATTERN,
                  message: "Enter a valid SSN (XXX-XX-XXXX)"
                }
              ]}
            >
              <TextInput
                type="password"
                autoComplete="off"
                disableAutoCapitalize
                placeholder={
                  initialValues?.ssn_last4
                    ? `On file, ending ${initialValues.ssn_last4}`
                    : "XXX-XX-XXXX"
                }
                size="large"
              />
            </Form.Item>
          ) : (
            <Form.Item<SubcontractorFormValues>
              label="EIN"
              name="ein"
              rules={[
                {
                  pattern: EIN_PATTERN,
                  message: "Enter a valid EIN (XX-XXXXXXX)"
                }
              ]}
            >
              <TextInput
                disableAutoCapitalize
                placeholder={
                  initialValues?.ein_last4
                    ? `On file, ending ${initialValues.ein_last4}`
                    : "XX-XXXXXXX"
                }
                size="large"
              />
            </Form.Item>
          )}

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="W-9 Document"
                name="w9_document"
                valuePropName="fileList"
                getValueFromEvent={normFile}
              >
                <DocumentUpload
                  buttonLabel={initialValues ? "Replace file" : "Select file"}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="W-9 Validity Date"
                name="w9_validity_date"
              >
                <CustomDatePicker
                  className={"w-full"}
                  size="large"
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item<SubcontractorFormValues>
                label="Primary Contact Name"
                name="primary_contact_name"
              >
                <TextInput placeholder="Enter contact name" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item<SubcontractorFormValues>
                label="Primary Contact Email"
                name="primary_contact_email"
                rules={[
                  { type: "email", message: "Please enter a valid email" }
                ]}
                normalize={normalizeEmail}
              >
                <TextInput
                  placeholder="Enter contact email"
                  size="large"
                  disableAutoCapitalize
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item<SubcontractorFormValues>
                label="Primary Contact Phone"
                name="primary_contact_phone"
                rules={[{ validator: validatePhone }]}
              >
                <PhoneInput placeholder="Enter contact phone" size="large" />
              </Form.Item>
            </Col>
          </Row>
        </>

        <>
          <Title level={5} className={"!mb-2 !mt-2"}>
            CSLB License
          </Title>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="License Number"
                name="cslb_license_number"
                rules={[
                  {
                    pattern: CSLB_LICENSE_NUMBER_PATTERN,
                    message:
                      "Enter a valid CSLB license number (up to 8 digits)"
                  }
                ]}
              >
                <TextInput
                  placeholder="Up to 8 digits"
                  size="large"
                  disableAutoCapitalize
                />
              </Form.Item>
            </Col>
            {initialValues && (
              <Col xs={24} md={12}>
                <Form.Item label="Expiration Date">
                  <Flex gap={8} align={"center"}>
                    <TextInput
                      disableAutoCapitalize
                      size="large"
                      disabled
                      className={"flex-1"}
                      value={
                        licenseExpirationOverride === undefined
                          ? (initialValues.cslb_expiration_date ??
                            "No License document on file")
                          : (licenseExpirationOverride ?? "No expiry date")
                      }
                    />
                    <Button onClick={() => setManagingLicense(true)}>
                      {t("subcontractorDetail.compliance.manageLicense")}
                    </Button>
                  </Flex>
                </Form.Item>
              </Col>
            )}
          </Row>

          {initialValues?.id && managingLicense && (
            <ComplianceDocumentUploadModal
              open
              subcontractorId={initialValues.id}
              documentType={"license"}
              currentExpirationDate={
                licenseExpirationOverride === undefined
                  ? initialValues.cslb_expiration_date
                  : licenseExpirationOverride
              }
              onClose={() => setManagingLicense(false)}
              onUploaded={(document) => {
                setLicenseExpirationOverride(document.expiration_date)
                setManagingLicense(false)
              }}
            />
          )}

          <Form.Item<SubcontractorFormValues>
            label="Classification"
            name="cslb_classification"
          >
            <SelectComponent
              size="large"
              mode={"multiple"}
              maxTagCount={"responsive"}
              placeholder="Select CSLB classification"
              options={filterOptions?.["specialization"] || []}
              optionFilterType="form"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input?.toLowerCase())
              }
            />
          </Form.Item>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="Status"
                name="cslb_status"
              >
                <SelectComponent
                  size="large"
                  placeholder="Select license status"
                  options={CSLB_STATUS_OPTIONS}
                  optionFilterType="form"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="Issue Date"
                name="cslb_issue_date"
              >
                <CustomDatePicker
                  className={"w-full"}
                  size="large"
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="Contractor Bond Amount"
                name="cslb_bond_amount"
                rules={[{ validator: validateBondAmount }]}
              >
                <InputNumber
                  size="large"
                  prefix="$"
                  style={{ width: "100%" }}
                  placeholder="25000"
                  min={0}
                  precision={2}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="Bond Number"
                name="cslb_bond_number"
              >
                <TextInput
                  placeholder="Enter bond number"
                  size="large"
                  disableAutoCapitalize
                />
              </Form.Item>
            </Col>
          </Row>

          <Text type="secondary" className={"mb-2 block"}>
            The CSLB also requires a separate bond for the qualifying individual
            when they own under 10% of the company.
          </Text>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="Qualifying Individual Bond Amount"
                name="cslb_qualifier_bond_amount"
                rules={[{ validator: validateBondAmount }]}
              >
                <InputNumber
                  size="large"
                  prefix="$"
                  style={{ width: "100%" }}
                  placeholder="25000"
                  min={0}
                  precision={2}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item<SubcontractorFormValues>
                label="Qualifying Individual Bond Number"
                name="cslb_qualifier_bond_number"
              >
                <TextInput
                  placeholder="Enter bond number"
                  size="large"
                  disableAutoCapitalize
                />
              </Form.Item>
            </Col>
          </Row>
        </>

        <>
          <Title level={5} className={"!mb-2 !mt-2"}>
            Payment
          </Title>
          <Form.Item<SubcontractorFormValues>
            label="Payment Method"
            name="payment_method"
          >
            <SelectComponent
              size="large"
              placeholder="Select payment method"
              options={PAYMENT_METHOD_OPTIONS}
              optionFilterType="form"
              allowClear
            />
          </Form.Item>

          {paymentMethod === "ach" && (
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item<SubcontractorFormValues>
                  label="ACH Routing Number"
                  name="ach_routing_number"
                  rules={[
                    {
                      pattern: ACH_ROUTING_NUMBER_PATTERN,
                      message: "Enter a valid 9-digit routing number"
                    }
                  ]}
                >
                  <TextInput
                    disableAutoCapitalize
                    placeholder={
                      initialValues?.ach_routing_number_last4
                        ? `On file, ending ${initialValues.ach_routing_number_last4}`
                        : "123456789"
                    }
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item<SubcontractorFormValues>
                  label="ACH Account Number"
                  name="ach_account_number"
                  rules={[
                    {
                      pattern: ACH_ACCOUNT_NUMBER_PATTERN,
                      message: "Enter a valid account number"
                    }
                  ]}
                >
                  <TextInput
                    type="password"
                    autoComplete="off"
                    disableAutoCapitalize
                    placeholder={
                      initialValues?.ach_account_number_last4
                        ? `On file, ending ${initialValues.ach_account_number_last4}`
                        : "Enter account number"
                    }
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
        </>
      </Flex>

      {/* Form Actions */}
      <Form.Item noStyle>
        <Flex align="center" justify="flex-end" gap="middle">
          <Button size="large" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="large"
            type="primary"
            htmlType="submit"
            loading={fetching || loading}
            className="min-w-44"
          >
            {initialValues
              ? "Save Changes"
              : sendInvitation
                ? "Send Invitation"
                : "Add Subcontractor"}
          </Button>
        </Flex>
      </Form.Item>
    </Form>
  )
}
